import { Component, type ErrorInfo, type ReactNode } from "react";
import NeedsConnection from "./NeedsConnection";
import { reportClientError } from "./ErrorBoundary";

type Props = {
  children: ReactNode;
  /** When true, render errors show NeedsConnection instead of crashing the app. */
  offline?: boolean;
};

type State = { failed: boolean };

/**
 * Catches render errors inside one secondary screen so a single bad open
 * (offline chunk edge cases, sparse cache) does not blank the whole app.
 */
export default class ScreenErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };
  reported = false;

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (!this.reported) {
      this.reported = true;
      reportClientError("client_render", error);
    }
  }

  componentDidUpdate(prev: Props) {
    if (prev.children !== this.props.children && this.state.failed) {
      this.reported = false;
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    if (this.props.offline) {
      return (
        <NeedsConnection
          title="Needs a connection"
          detail="This section couldn’t open while offline. Reconnect and try again."
        />
      );
    }
    return (
      <NeedsConnection
        title="Couldn’t open this section"
        detail="Something failed while opening this screen. Your saved entries are safe — go back and try again."
      />
    );
  }
}
